import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import Sidebar from './components/Sidebar';
import Header from './components/Header';
import MenuToggle from './components/MenuToggle';
import Footer from './components/Footer';
import Home from './pages/Home';
import { AppKitProvider } from './config/appkit.jsx';
import { AuthProvider } from './context/AuthContext';
import { SidebarProvider } from './context/SidebarContext';
import WalletConnectionManager from './components/WalletConnectionManager';
import PassportCallback from './components/PassportCallback';
import PassportLogout from './components/PassportLogout';
// Removed WalletUpdatesDebug import
// Removed WalletBalanceProvider import
import Profile from './pages/Profile';
import Live from './pages/Live';
import BitSportLive from './pages/BitSportLive';
import Tournaments from './pages/Tournaments';
import TournamentDetails from './pages/TournamentDetails';
import CreateStream from './pages/CreateStream';
import CreateTournament from './pages/CreateTournament';
import JoinTournament from './pages/JoinTournament';
import Privacy from './pages/Privacy';
import Terms from './pages/Terms';
import Wallet from './pages/Wallet';
import Presale from './pages/Presale';
import BitPoolAIAgent from './pages/BitPoolAIAgent';
import Admin from './pages/Admin';
import AdminLogin from './pages/AdminLogin';
import QuestDetails from './components/quests/QuestDetails';
import CreateQuest from './components/quests/CreateQuest';
import Duel from './pages/Duel';
import BitPool from './pages/BitPool';
import Settings from './pages/Settings'; // Import the new Settings page
import NFTCollection from './pages/NFTCollection'; // Import the new NFT Collection page
import NFTDetails from './pages/NFTDetails'; // Import the new NFT Details page
import NFTEdit from './pages/NFTEdit'; // Import the new NFT Edit page
import ProtectedAdminRoute from './components/ProtectedAdminRoute';
import ProtectedRoute from './components/ProtectedRoute';
import { useAuth } from './context/AuthContext';

// Create a wrapper component to access the user from auth context
function AppContent() {
  const { user } = useAuth();

  return (
      <div className="flex relative">
        <WalletConnectionManager />
        <Toaster
          position="top-right"
          toastOptions={{
            className: '',
            duration: 5000,
            style: {
              background: '#1A1A1A',
              color: '#fff',
              border: '1px solid rgba(139, 92, 246, 0.3)',
              boxShadow: '0 0 10px rgba(139, 92, 246, 0.15)',
            },
            success: {
              duration: 3000,
              iconTheme: {
                primary: '#10B981',
                secondary: '#fff',
              },
              style: {
                background: '#1A1A1A',
                color: '#fff',
                border: '1px solid rgba(16, 185, 129, 0.5)',
                boxShadow: '0 0 10px rgba(16, 185, 129, 0.15)',
              },
            },
            error: {
              duration: 4000,
              iconTheme: {
                primary: '#EF4444',
                secondary: '#fff',
              },
              style: {
                background: '#1A1A1A',
                color: '#fff',
                border: '1px solid rgba(239, 68, 68, 0.5)',
                boxShadow: '0 0 10px rgba(239, 68, 68, 0.15)',
              },
            },
            loading: {
              duration: Infinity,
              style: {
                background: '#1A1A1A',
                color: '#fff',
                border: '1px solid rgba(139, 92, 246, 0.3)',
                boxShadow: '0 0 10px rgba(139, 92, 246, 0.15)',
              },
            },
          }}
        />
        <Sidebar />
        <MenuToggle />
        <Header />
        <main className="flex-1 md:ml-80 min-h-screen bg-dark relative z-0 pt-16 md:pt-0 overflow-hidden">
          {/* Main background effects */}
          <div className="fixed inset-0 z-0 pointer-events-none">
            <div className="absolute -top-[10%] -left-[10%] w-[50%] h-[50%] bg-purple-900/5 rounded-full filter blur-[120px]"></div>
            <div className="absolute -bottom-[10%] -right-[10%] w-[50%] h-[50%] bg-violet-800/5 rounded-full filter blur-[120px]"></div>
            <div className="absolute top-1/4 right-1/4 w-[30%] h-[30%] bg-violet-600/3 rounded-full filter blur-[80px] animate-pulse-slow"></div>
          </div>
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<Home />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/presale" element={<Presale />} />

            {/* Protected Routes */}
            <Route path="/profile/settings" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
            <Route path="/:username" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
            <Route path="/live/:id" element={<Live />} />
            <Route path="/bitsport-live" element={<ProtectedRoute><BitSportLive /></ProtectedRoute>} />
            <Route path="/tournaments" element={<ProtectedRoute><Tournaments /></ProtectedRoute>} />
            {/* Make Tournament Details public */}
            <Route path="/tournament/:id" element={<TournamentDetails />} />
            <Route path="/create-tournament" element={<ProtectedRoute><CreateTournament /></ProtectedRoute>} />
            <Route path="/join-tournament" element={<ProtectedRoute><JoinTournament /></ProtectedRoute>} />
            <Route path="/create-stream" element={<ProtectedRoute><CreateStream /></ProtectedRoute>} />
            <Route path="/wallet" element={<ProtectedRoute><Wallet /></ProtectedRoute>} />
            <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} /> {/* Add Settings route */}

            {/* BitPool Routes */}
            <Route path="/bitpool" element={<BitPool />} />
            <Route path="/quests" element={<BitPoolAIAgent />} />
            <Route path="/quests/:id" element={<QuestDetails />} />
            <Route path="/duel" element={<Duel />} />
            <Route path="/duel/:roomId" element={<Duel />} />

            {/* NFT Collection Routes */}
            <Route path="/nft-collection" element={<NFTCollection />} />
            <Route path="/nft/:id" element={<NFTDetails />} />
            <Route path="/nft/:id/edit" element={<ProtectedAdminRoute><NFTEdit /></ProtectedAdminRoute>} />

            {/* Admin Routes */}
            <Route path="/admin-login" element={<AdminLogin />} />
            <Route path="/admin" element={<ProtectedAdminRoute><Admin /></ProtectedAdminRoute>} />
            <Route path="/admin/create-quest" element={<ProtectedAdminRoute><CreateQuest /></ProtectedAdminRoute>} />

            {/* P2Pool Redirect - This is needed to handle direct navigation to p2pool URLs */}
            <Route path="/p2pool/*" element={
              <Navigate to="/bitpool" replace />
            } />

            {/* Passport Routes */}
            <Route path="/passport-callback" element={<PassportCallback />} />
            <Route path="/passport-logout" element={<PassportLogout />} />
          </Routes>
          <Footer />
        </main>
      </div>
  );
}

function App() {
  return (
    <Router>
      <AppKitProvider>
        <SidebarProvider>
          <AuthProvider>
            <AppContent />
          </AuthProvider>
        </SidebarProvider>
      </AppKitProvider>
    </Router>
  );
}

export default App;
